import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { TypedMemo } from '../../../common/types';
import { ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { useIsNativeEmbed } from '../../../music24/navigation/hooks/useIsNativeEmbed';
import { catalogConnectPath, HANDOFF_PATH } from '../../../mobileApps/mobileAppLinks';
import { nativeCatalogConnectDeepLink, openNativeLink } from '../nativeConnect';
import { soundCloudInsightsNoteState } from './soundCloudInsightsNoteState';
import { soundCloudInsightsMessages as m } from './soundCloudInsightsMessages';
/* eslint-disable-next-line css-modules/no-unused-class -- shared stylesheet; this part uses its insights slice */
import own from './SoundCloudCard.module.scss';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';

interface SoundCloudInsightsNoteProps {
  connection: ServiceConnection;
  /** The state of the card (`soundCloudCardState`). */
  cardState: string;
}

/** The handoff page for another device, then the app opens Catalog on SoundCloud. */
const HANDOFF_URL = `${HANDOFF_PATH}?next=${encodeURIComponent(catalogConnectPath(MusicAPIIntegrationID.SoundCloud))}`;

/**
 * The note under the sync line of the web SoundCloud card: "Use the Music24 app to get more
 * SoundCloud data" (design soundcloud-insights b1). Inside the app's web view the link opens the
 * native connect sheet; in a browser it opens the phone handoff.
 */
const SoundCloudInsightsNoteComponent: React.FC<SoundCloudInsightsNoteProps> = ({ connection, cardState }) => {
  const intl = useIntl();
  const isNativeEmbed = useIsNativeEmbed();
  const state = soundCloudInsightsNoteState(connection, cardState);
  const openApp = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (!isNativeEmbed) {
        return;
      }
      event.preventDefault();
      openNativeLink(nativeCatalogConnectDeepLink(MusicAPIIntegrationID.SoundCloud));
    },
    [isNativeEmbed]
  );
  if (state === null) {
    return null;
  }
  const link = state === 'none' ? m.getApp : state === 'expired' ? m.openApp : null;
  return (
    <div
      className={clsx(
        own.insightsNote,
        state === 'connected' && own.insightsNoteOk,
        state === 'expired' && own.insightsNoteWarn
      )}
      data-testid="soundcloud-insights-note"
      data-state={state}
    >
      <span className={own.insightsDot} />
      <div className={own.insightsText}>
        <span>{withBrandWord(intl.formatMessage(m[state]))}</span>
        {link !== null && (
          <a href={HANDOFF_URL} onClick={openApp} className={own.insightsLink}>
            {intl.formatMessage(link)}
          </a>
        )}
      </div>
    </div>
  );
};

export const SoundCloudInsightsNote = TypedMemo(SoundCloudInsightsNoteComponent);
